import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useOverdue } from '../../contexts/OverdueContext';
import { useSupportNotif } from '../../contexts/SupportNotifContext';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import NewsDropdown from '../NewsDropdown';
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  Users,
  Package,
  CheckSquare,
  CalendarDays,
  BarChart3,
  Settings,
  LogOut,
  PenTool,
  Menu,
  X,
  Moon,
  Sun,
  Monitor,
  HelpCircle,
  Shield,
  Crown,
  Sparkles,
  Wallet,
  Globe,
  BookOpen,
  ArrowRight,
  Search,
  Receipt,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Дашборд' },
  { to: '/form-builder', icon: PenTool, label: 'Конструктор форм' },
  { to: '/orders', icon: ShoppingCart, label: 'Заказы' },
  { to: '/customers', icon: Users, label: 'Клиенты' },
  { to: '/materials', icon: Package, label: 'Материалы' },
  { to: '/tasks', icon: CheckSquare, label: 'Задачи' },
  { to: '/calendar', icon: CalendarDays, label: 'Календарь' },
  { to: '/reports', icon: BarChart3, label: 'Отчёты' },
  { to: '/finance', icon: Wallet, label: 'Финансы' },
  { to: '/invoices', icon: Receipt, label: 'Счета' },
  { to: '/my-page', icon: Globe, label: 'Моя страница' },
  { to: '/support', icon: HelpCircle, label: 'Поддержка' },
  { to: '/help', icon: BookOpen, label: 'Справка' },
];

export function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { overdueCount } = useOverdue();
  const { unreadCount: supportUnread } = useSupportNotif();
  const navigate = useNavigate();

  // Build nav items dynamically based on role
  const allNavItems = [
    ...navItems,
    ...(user?.role === 'admin' ? [{ to: '/admin', icon: Shield, label: 'Админ-панель', isAdmin: true }] : []),
  ];

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    const isMobile = window.innerWidth < 1024;
    if (isOpen && isMobile) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay - blocks touch events on background */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
          onTouchMove={(e) => e.preventDefault()}
          style={{ touchAction: 'none' }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky top-0 left-0 z-50 w-64 bg-card border-r border-border transition-transform duration-300",
          "lg:translate-x-0 h-[100dvh]",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{ overscrollBehavior: 'contain' }}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-6 py-3 flex items-center justify-between shrink-0">
            <div>
              <img 
                src="/logo-light.svg" 
                alt="YaDela" 
                className="h-14 w-auto dark:hidden"
              />
              <img 
                src="/logo-dark.svg" 
                alt="YaDela" 
                className="h-14 w-auto hidden dark:block"
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onClose}
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <Separator className="shrink-0" />

          {/* Scrollable navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {allNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? item.isAdmin ? "bg-red-600 text-white" : "bg-primary text-primary-foreground"
                      : item.isAdmin ? "text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )
                }
                data-testid={`nav-${item.to.replace('/', '') || 'dashboard'}`}
              >
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="flex-1">{item.label}</span>
                {item.to === '/tasks' && overdueCount > 0 && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-white leading-none">
                    {overdueCount > 99 ? '99+' : overdueCount}
                  </span>
                )}
                {item.to === '/support' && supportUnread > 0 && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[10px] font-bold text-white leading-none">
                    {supportUnread > 99 ? '99+' : supportUnread}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Fixed bottom section */}
          <div className="shrink-0 border-t border-border" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            {/* Theme + Settings row */}
            <div className="px-3 pt-2 flex items-center gap-1">
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium transition-colors text-muted-foreground hover:bg-muted hover:text-foreground flex-1"
                data-testid="theme-toggle-btn"
              >
                {theme === 'system' ? (
                  <><Monitor className="h-4 w-4" strokeWidth={1.5} /> Авто</>
                ) : theme === 'dark' ? (
                  <><Moon className="h-4 w-4" strokeWidth={1.5} /> Тёмная</>
                ) : (
                  <><Sun className="h-4 w-4" strokeWidth={1.5} /> Светлая</>
                )}
              </button>

              <NavLink
                to="/settings"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium transition-colors flex-1",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )
                }
                data-testid="nav-settings"
              >
                <Settings className="h-4 w-4" strokeWidth={1.5} />
                Настройки
              </NavLink>
            </div>

            {/* User info + Plan + Logout */}
            <div className="px-3 pb-2 pt-1.5">
              <div className="flex items-center gap-2 px-2.5 py-1">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="shrink-0 p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
                  data-testid="logout-btn"
                  title="Выйти"
                >
                  <LogOut className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>

              {/* Plan Badge */}
              <NavLink
                to="/pricing"
                onClick={onClose}
                className="block mt-2"
                data-testid="nav-pricing"
              >
                {user?.plan === 'pro' ? (
                  <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/35 hover:from-emerald-500/30 hover:to-teal-500/30 transition-all">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/25">
                      <Crown className="h-4.5 w-4.5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Тариф Про</p>
                      <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/80">Безлимитный доступ</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/45 hover:from-amber-500/30 hover:to-orange-500/30 hover:border-amber-400/60 transition-all group">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500/25">
                      <Sparkles className="h-4.5 w-4.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Демо-версия</p>
                      <p className="text-[11px] text-amber-600/80 dark:text-amber-400/80">Перейти на Про →</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-amber-500/60 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </div>
                )}
              </NavLink>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

export function MobileHeader({ onMenuClick, onSearchClick }) {
  return (
    <header className="sticky top-0 z-30 lg:hidden bg-card border-b border-border px-4 py-3 flex items-center relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={onMenuClick}
        data-testid="open-sidebar-btn"
        className="z-10"
      >
        <Menu className="h-5 w-5" />
      </Button>
      <h1 className="absolute left-0 right-0 flex justify-center pointer-events-none">
        <img 
          src="/logo-light.svg" 
          alt="YaDela" 
          className="h-10 w-auto dark:hidden"
        />
        <img 
          src="/logo-dark.svg" 
          alt="YaDela" 
          className="h-10 w-auto hidden dark:block"
        />
      </h1>
      <div className="ml-auto z-10 flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={onSearchClick} className="h-9 w-9">
          <Search className="h-5 w-5" />
        </Button>
        <NewsDropdown />
      </div>
    </header>
  );
}
